import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Checkbox,
    Box,
    Typography,
    InputAdornment,
    IconButton,
    Paper,
    Divider
} from '@mui/material';
import { toast } from 'sonner';
import { Iconify } from 'src/index';

export default function StandaloneManageColumnsModal({
    show,
    onHide,
    tableName,
    columns,
    columnVisibility,
    onColumnVisibilityChange,
    excludeColumns = []
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [localColumnVisibility, setLocalColumnVisibility] = useState({});
    const [selectAll, setSelectAll] = useState(true);

    useEffect(() => {
        if (columns && columns.length > 0) {
            const initialVisibility = {};
            columns.forEach((column, idx) => {
                initialVisibility[idx] = columnVisibility[idx] !== false;
            });
            setLocalColumnVisibility(initialVisibility);

            const allVisible = Object.values(initialVisibility).every(visible => visible);
            setSelectAll(allVisible);
        }
    }, [columns, columnVisibility]);

    const handleColumnToggle = (columnIndex) => {
        const newVisibility = { ...localColumnVisibility };
        newVisibility[columnIndex] = !newVisibility[columnIndex];
        setLocalColumnVisibility(newVisibility);

        const allVisible = Object.values(newVisibility).every(visible => visible);
        setSelectAll(allVisible);
    };

    const handleSelectAllToggle = (checked) => {
        const newVisibility = {};
        columns.forEach((_, idx) => {
            newVisibility[idx] = checked;
        });
        setLocalColumnVisibility(newVisibility);
        setSelectAll(checked);
    };

    const handleReset = () => {
        const defaultVisibility = {};
        columns.forEach((_, idx) => {
            defaultVisibility[idx] = true;
        });
        setLocalColumnVisibility(defaultVisibility);
        setSelectAll(true);
        onColumnVisibilityChange(defaultVisibility);
    };

    const filteredColumns = columns.filter(column =>
        !excludeColumns.includes(column.header) &&
        column.header.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const visibleCount = Object.values(localColumnVisibility).filter(v => v).length;

    if (!columns || columns.length === 0) {
        return null;
    }

    const handleApplyChanges = () => {
        onColumnVisibilityChange(localColumnVisibility);
        onHide();
    };

    return (
        <Dialog
            open={show}
            onClose={onHide}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.2)',
                    maxWidth: '400px',
                    width: '100%'
                }
            }}
        >
            <DialogTitle sx={{my:-1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography className="page-title">
                    Manage Columns
                </Typography>
                <IconButton onClick={onHide} size="small">
                    <Iconify icon="mdi:close" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ px: 3, pb: 2 }}>
                <Box sx={{ mb: 1}}>
                    <TextField
                        fullWidth
                        className="input-border"
                        size="small"
                        placeholder="Search columns..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-outline" />
                                    </InputAdornment>
                                )
                            }
                        }}
                     
                    />
                </Box>

                <Box sx={{ mb:1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={selectAll}
                            onChange={(e) => handleSelectAllToggle(e.target.checked)}
                            size="small"
                            className="checkBox"
                        />
                        <Typography sx={{ fontSize: '0.875rem', color: '#212b36', ml: 0.5 }}>
                            Show/Hide All
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem'}}>
                        {visibleCount} of {columns.length} visible
                    </Typography>
                </Box>

                <Paper
                    variant="outlined" 
                    sx={{
                        maxHeight: 300,
                        overflowY: 'auto',
                        p: 1.5,
                        border: '1px solid rgba(145, 158, 171, 0.16)',
                        borderRadius: 1
                    }}
                >
                    {filteredColumns.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>
                                No columns found matching "{searchTerm}"
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            {filteredColumns.map((column) => {
                                const actualIndex = columns.findIndex(col => col.header === column.header);
                                return (
                                    <Box
                                        key={actualIndex}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 1.5,
                                            '&:last-child': { mb: 0 }
                                        }}
                                    >
                                        <Checkbox
                                            checked={localColumnVisibility[actualIndex] || false}
                                            onChange={() => handleColumnToggle(actualIndex)}
                                            size="small"
                                            sx={{ p: 0.5 ,
                                                color: '#F8991D',
                                                '&.Mui-checked': {
                                                    color: '#F8991D',
                                                },
                                            }}
                                        />
                                        <Typography
                                            onClick={() => handleColumnToggle(actualIndex)}
                                            sx={{
                                                fontSize: '0.875rem',
                                                color: '#212b36',
                                                cursor: 'pointer',
                                                flexGrow: 1,
                                                ml: 0.5
                                            }}
                                        >
                                            {column.header}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Paper>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    size="medium"
                    startIcon={<Iconify icon="mdi:refresh" width={18} />}
                    className="button-variant-outlined"
                >
                    Reset All
                </Button>
                <Button
                    variant="contained"
                    onClick={handleApplyChanges}
                    size="medium"
                    startIcon={<Iconify icon="mdi:check" width={18} />}
                    className="button-variant-contained"
                >
                    Apply Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
}
